import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Clock3, Eye, X } from "lucide-react";
import AdminLayout from "./AdminLayout";
import AdminUserLink from "../components/AdminUserLink";
import API from "../services/api";

function AdminStarline() {
  const [markets, setMarkets] = useState([]);
  const [slots, setSlots] = useState([]);
  const [results, setResults] = useState([]);
  const [validPannas, setValidPannas] = useState([]);
  const [form, setForm] = useState({
    result_date: getBusinessResultDate(),
    market_id: "",
    result_hour: "10:00 AM",
    pana: "",
    digit: "",
  });
  const [preview, setPreview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const panaSuggestions = useMemo(() => getPanaSuggestions(form.pana, validPannas), [form.pana, validPannas]);

  const selectedMarket = useMemo(
    () => markets.find((item) => String(item.id) === String(form.market_id)),
    [form.market_id, markets]
  );
  const selectedSlots = useMemo(
    () => slots.filter((item) => String(item.market_id) === String(form.market_id) && item.is_active !== 0),
    [form.market_id, slots]
  );
  const declaredSlotSet = useMemo(() => {
    return new Set(
      results
        .filter(
          (item) =>
            String(item.market_id) === String(form.market_id) &&
            String(item.result_date || "").slice(0, 10) === form.result_date
        )
        .map((item) => String(item.result_hour || "").trim().toUpperCase())
    );
  }, [form.market_id, form.result_date, results]);
  const dateResults = useMemo(() => {
    return results
      .filter((item) => String(item.result_date || "").slice(0, 10) === form.result_date)
      .sort((left, right) => {
        if (String(left.market_name || "").localeCompare(String(right.market_name || "")) !== 0) {
          return String(left.market_name || "").localeCompare(String(right.market_name || ""));
        }
        return toSlotMinutes(left.result_hour) - toSlotMinutes(right.result_hour);
      });
  }, [form.result_date, results]);
  const marketDateResults = useMemo(() => {
    return results
      .filter(
        (item) =>
          String(item.market_id) === String(form.market_id) &&
          String(item.result_date || "").slice(0, 10) === form.result_date
      )
      .sort((left, right) => toSlotMinutes(left.result_hour) - toSlotMinutes(right.result_hour));
  }, [form.market_id, form.result_date, results]);
  const otherMarketDateResults = useMemo(() => {
    return dateResults.filter((item) => String(item.market_id) !== String(form.market_id));
  }, [dateResults, form.market_id]);
  const declaredTimeline = useMemo(() => {
    return selectedSlots.map((slot) => {
      const slotValue = String(slot.time_slot || "").trim();
      const match = marketDateResults.find(
        (item) => String(item.result_hour || "").trim().toUpperCase() === slotValue.toUpperCase()
      );
      return { slotValue, result: match || null };
    });
  }, [marketDateResults, selectedSlots]);

  useEffect(() => {
    const run = async () => {
      await Promise.all([loadMarkets(), loadResults(), loadValidPannas()]);
    };
    run();
  }, []);

  useEffect(() => {
    if (!selectedSlots.length) return;
    if (form.result_hour && !declaredSlotSet.has(String(form.result_hour).trim().toUpperCase())) return;
    const nextHour = getFirstAvailableSlot(selectedSlots, declaredSlotSet);
    if (!nextHour || nextHour === form.result_hour) return;
    setForm((current) => ({ ...current, result_hour: nextHour }));
  }, [declaredSlotSet, form.result_hour, selectedSlots]);

  const loadMarkets = async () => {
    try {
      const res = await API.get("/admin/starline-control");
      const starlineMarkets = Array.isArray(res.data?.markets) ? res.data.markets : [];
      const starlineSlots = Array.isArray(res.data?.slots) ? res.data.slots : [];
      setMarkets(starlineMarkets);
      setSlots(starlineSlots);
      setForm((current) => ({
        ...current,
        market_id: current.market_id || (starlineMarkets[0]?.id ? String(starlineMarkets[0].id) : ""),
        result_hour: current.result_hour || getFirstActiveSlot(starlineSlots, current.market_id || starlineMarkets[0]?.id) || "09:00 AM",
      }));
    } catch {
      setMarkets([]);
    }
  };

  const loadResults = async () => {
    try {
      const res = await API.get("/admin/starline-results");
      setResults(Array.isArray(res.data) ? res.data : []);
    } catch {
      setResults([]);
    }
  };

  const loadValidPannas = async () => {
    try {
      const res = await API.get("/admin/valid-pannas");
      const values = Array.isArray(res.data) ? res.data.map((item) => String(item.pana || "")) : [];
      setValidPannas(values.filter(Boolean));
    } catch {
      setValidPannas([]);
    }
  };

  const saveResult = async () => {
    if (!form.result_hour) { alert("Select an available time slot"); return; }
    try {
      await API.post("/admin/starline-results", {
        ...form,
        digit: form.digit || calculateDigit(form.pana),
      });
      await loadResults();
      setForm((current) => ({ ...current, pana: "", digit: "" }));
      alert("Starline result saved");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save starline result");
    }
  };

  const deleteResult = async (id) => {
    try {
      await API.delete(`/admin/starline-results/${id}`);
      await loadResults();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to delete starline result");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveResult();
  };

  const previewWinners = async () => {
    if (!form.market_id || !form.result_date || !form.result_hour || !form.pana) {
      alert("Fill all required fields");
      return;
    }
    try {
      const res = await API.post("/admin/starline-results/preview", {
        ...form,
        digit: form.digit || calculateDigit(form.pana),
      });
      setPreview(res.data || null);
      setShowModal(true);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to preview winners");
    }
  };

  const handleMarketChange = (marketId) => {
    const marketSlots = selectedSlotsForMarket(slots, marketId);
    const nextDeclaredSet = new Set(
      results
        .filter((item) => String(item.market_id) === String(marketId) && String(item.result_date || "").slice(0, 10) === form.result_date)
        .map((item) => String(item.result_hour || "").trim().toUpperCase())
    );
    const nextHour = getFirstAvailableSlot(marketSlots, nextDeclaredSet);
    setForm((current) => ({ ...current, market_id: marketId, result_hour: nextHour || "" }));
  };

  const handleDateChange = (date) => {
    const nextDeclaredSet = new Set(
      results
        .filter((item) => String(item.market_id) === String(form.market_id) && String(item.result_date || "").slice(0, 10) === date)
        .map((item) => String(item.result_hour || "").trim().toUpperCase())
    );
    const nextHour = getFirstAvailableSlot(selectedSlots, nextDeclaredSet);
    setForm((current) => ({ ...current, result_date: date, result_hour: nextHour || current.result_hour || "" }));
  };

  return (
    <AdminLayout>
      <div style={page}>
        <div>
          <h2 style={title}>Starline - Declare Result</h2>
          <p style={subtitle}>Declare starline results with market and hourly slot</p>
        </div>

        <section style={card}>
          <h3 style={sectionTitle}>Declare Result</h3>

          <form onSubmit={handleSubmit}>
            <div style={formGrid}>
              <label style={field}>
                <span>Date</span>
                <input type="date" value={form.result_date} onChange={(e) => handleDateChange(e.target.value)} style={input} />
              </label>

              <label style={field}>
                <span>Market</span>
                <div style={marketSelectWrap}>
                  <select value={form.market_id} onChange={(e) => handleMarketChange(e.target.value)} style={marketSelect}>
                    <option value="">Select market</option>
                    {markets.map((market) => (
                      <option key={market.id} value={market.id}>{market.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={18} color="#64748b" />
                </div>
              </label>

              <label style={field}>
                <span>Panna</span>
                <div style={panaBox}>
                  <input
                    value={form.pana}
                    list="admin-starline-panna-list"
                    onChange={(e) => {
                      const pana = e.target.value.replace(/[^\d]/g, "").slice(0, 3);
                      setForm((c) => ({ ...c, pana, digit: calculateDigit(pana) }));
                    }}
                    style={panaInput}
                    placeholder="Type or select panna"
                  />
                  <datalist id="admin-starline-panna-list">
                    {validPannas.map((pana) => <option key={pana} value={pana} />)}
                  </datalist>
                  <div style={panaSuggestionGrid}>
                    {panaSuggestions.slice(0, 18).map((pana) => (
                      <button
                        key={pana}
                        type="button"
                        style={panaChip}
                        onClick={() => setForm((c) => ({ ...c, pana, digit: calculateDigit(pana) }))}
                      >
                        <strong>{pana}</strong>
                        <span>{calculateDigit(pana)}</span>
                      </button>
                    ))}
                    {!panaSuggestions.length && <span style={panaEmpty}>No panna found</span>}
                  </div>
                </div>
              </label>

              <label style={field}>
                <span>Digit</span>
                <input
                  value={form.digit}
                  onChange={(e) => setForm((c) => ({ ...c, digit: e.target.value.replace(/[^\d]/g, "").slice(0, 2) }))}
                  style={input}
                  placeholder="7"
                />
              </label>
            </div>

            <div style={slotSection}>
              <div style={slotTitleRow}>
                <strong style={slotTitle}>Time Slot</strong>
                <span style={slotHint}>Declared time is disabled</span>
              </div>
              <div style={slotButtonGrid}>
                {selectedSlots.map((slot) => {
                  const slotValue = String(slot.time_slot || "").trim();
                  const declared = declaredSlotSet.has(slotValue.toUpperCase());
                  const active = String(form.result_hour) === slotValue;
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      style={declared ? slotButtonDone : active ? slotButtonActive : slotButton}
                      disabled={declared}
                      onClick={() => setForm((current) => ({ ...current, result_hour: slotValue }))}
                    >
                      <span style={slotButtonTime}>{slotValue}</span>
                      <span style={declared ? slotButtonStateDone : active ? slotButtonStateActive : slotButtonState}>
                        {declared ? "Declared" : active ? "Selected" : "Open"}
                      </span>
                    </button>
                  );
                })}
              </div>
              <label style={field}>
                <div style={timeSelectWrap}>
                  <Clock3 size={18} color="#2563eb" />
                  <select value={form.result_hour} onChange={(e) => setForm((current) => ({ ...current, result_hour: e.target.value }))} style={timeSelect}>
                    <option value="">Select Time</option>
                    {selectedSlots.map((slot) => {
                      const declared = declaredSlotSet.has(String(slot.time_slot || "").trim().toUpperCase());
                      return (
                        <option key={slot.id} value={slot.time_slot} disabled={declared}>
                          {`${slot.time_slot}${declared ? " - Declared" : ""}`}
                        </option>
                      );
                    })}
                  </select>
                  <ChevronDown size={18} color="#64748b" />
                </div>
                {!!form.result_hour && <div style={timePreviewChip}>Selected: {form.result_hour}</div>}
              </label>
            </div>

            <div style={buttonRow}>
              <button type="button" style={showButton} onClick={previewWinners} disabled={!selectedMarket || !form.result_hour}>
                <Eye size={18} /> Show Winners
              </button>
              <button type="submit" style={saveButton}>Declare & Credit</button>
            </div>
          </form>
        </section>

        <section style={card}>
          <div style={tableTitleRow}>
            <h3 style={sectionTitle}>Declared Results - {form.result_date.split("-").reverse().join("/")}</h3>
            <span style={countText}>{dateResults.length} results</span>
          </div>
          {!!selectedMarket && (
            <div style={timelinePanel}>
              <div style={timelineHeader}>
                <div>
                  <strong style={timelineTitle}>{selectedMarket.name} Timeline</strong>
                  <div style={timelineSubtext}>Time-wise declared result status for the selected market</div>
                </div>
                <span style={timelineCountChip}>{marketDateResults.length}/{selectedSlots.length} declared</span>
              </div>
              <div style={timelineGrid}>
                {declaredTimeline.map((item) => (
                  <article key={item.slotValue} style={item.result ? declaredTimelineCardDone : declaredTimelineCard}>
                    <div style={declaredTimelineTop}>
                      <span style={item.result ? declaredTimelineTimeDone : declaredTimelineTime}>{item.slotValue}</span>
                      <span style={item.result ? declaredTimelineBadgeDone : declaredTimelineBadgePending}>
                        {item.result ? "Done" : "Pending"}
                      </span>
                    </div>
                    <div style={declaredTimelineBody}>
                      {item.result ? (
                        <>
                          <strong style={declaredTimelineResult}>{`${item.result.pana}-${item.result.digit}`}</strong>
                          <button type="button" style={declaredTimelineDelete} onClick={() => deleteResult(item.result.id)}>Delete</button>
                        </>
                      ) : (
                        <strong style={declaredTimelinePending}>Waiting for declaration</strong>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
          <div style={declaredGrid}>
            {otherMarketDateResults.map((item, index) => (
              <article key={item.id} style={declaredCard}>
                <div style={declaredTop}>
                  <span style={declaredIndex}>#{index + 1}</span>
                  <span style={declaredMarket}>{item.market_name || `Market ${item.market_id}`}</span>
                  <span style={declaredTime}>{item.result_hour}</span>
                </div>
                <div style={declaredBottom}>
                  <strong style={declaredResult}>{`${item.pana}-${item.digit}`}</strong>
                  <span style={declaredDoneChip}>Declared</span>
                  <button type="button" style={deleteButton} onClick={() => deleteResult(item.id)}>Delete</button>
                </div>
              </article>
            ))}
            {!otherMarketDateResults.length ? <div style={emptyResultCard}>No extra declared results for other markets on this date yet.</div> : null}
          </div>
        </section>

        {showModal && preview && (
          <div style={modalBackdrop} onClick={() => setShowModal(false)}>
            <div style={modalCard} onClick={(e) => e.stopPropagation()}>
              <div style={modalHeader}>
                <div>
                  <div style={modalTitle}>Starline Winners Preview</div>
                  <div style={modalSubtitle}>{`${preview.winners || 0} winners - Rs ${Number(preview.total_payout || 0).toFixed(2)} total`}</div>
                </div>
                <button type="button" style={closeIcon} onClick={() => setShowModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div style={modalBody}>
                <table style={table}>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Game</th>
                      <th>Number</th>
                      <th>Bid</th>
                      <th>Win</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.winner_rows || []).map((row, index) => (
                      <tr key={`${row.user_id}-${index}`}>
                        <td><AdminUserLink userId={row.user_id} name={row.user_name} phone={row.user_phone} /></td>
                        <td>{row.game}</td>
                        <td style={valueStrong}>{row.number}</td>
                        <td>{`Rs ${Number(row.bid_amount || 0).toFixed(2)}`}</td>
                        <td style={winValue}>{`Rs ${Number(row.win_amount || 0).toFixed(2)}`}</td>
                      </tr>
                    ))}
                    {!preview.winner_rows?.length && (
                      <tr><td colSpan={5} style={emptyRow}>No winners for this result.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div style={modalFooter}>
                <button type="button" style={closeButton} onClick={() => setShowModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function selectedSlotsForMarket(slots, marketId) {
  return slots.filter((item) => String(item.market_id) === String(marketId) && item.is_active !== 0);
}

function getFirstActiveSlot(slots, marketId) {
  return selectedSlotsForMarket(slots, marketId)[0]?.time_slot || "";
}

function getFirstAvailableSlot(slotList, declaredSet) {
  return slotList.find((slot) => !declaredSet.has(String(slot.time_slot || "").trim().toUpperCase()))?.time_slot || "";
}

function toSlotMinutes(value) {
  const text = String(value || "").trim().toUpperCase();
  const match = text.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  let hour = Number(match[1]) % 12;
  const minute = Number(match[2]);
  if (match[3] === "PM") hour += 12;
  return hour * 60 + minute;
}

const calculateDigit = (pana) => {
  const digits = String(pana || "").replace(/\D/g, "").slice(0, 3).split("").map(Number);
  if (!digits.length) return "";
  return String(digits.reduce((sum, digit) => sum + digit, 0) % 10);
};

const getPanaSuggestions = (query, validPannas = []) => {
  const clean = String(query || "").replace(/\D/g, "").slice(0, 3);
  return Array.from(new Set(validPannas))
    .filter((pana) => !clean || String(pana).includes(clean))
    .sort((a, b) => String(a).localeCompare(String(b)));
};

const getBusinessResultDate = () => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", hour12: false,
  }).formatToParts(now);
  const map = Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
  const base = new Date(Date.UTC(Number(map.year), Number(map.month) - 1, Number(map.day)));
  if (Number(map.hour) < 2) base.setUTCDate(base.getUTCDate() - 1);
  return base.toISOString().slice(0, 10);
};

const page = { display: "grid", gap: "22px" };
const title = { margin: 0, fontSize: 22, fontWeight: 800, color: "#1e1b4b" };
const subtitle = { margin: "6px 0 0", color: "#64748b" };
const card = { background: "linear-gradient(180deg,#ffffff 0%, #f8fbff 100%)", border: "1px solid #dbe7f5", borderRadius: "22px", padding: "24px", boxShadow: "0 16px 34px rgba(15, 23, 42, 0.07)" };
const sectionTitle = { margin: 0, fontSize: "18px", fontWeight: 800 };
const formGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "16px", marginTop: "24px" };
const field = { display: "grid", gap: "8px", color: "#334155", fontWeight: 700 };
const input = { height: "58px", border: "1px solid #cbd5e1", borderRadius: "12px", padding: "0 14px", fontSize: "16px", background: "#fff" };
const panaBox = { display: "grid", gap: "8px" };
const panaInput = { ...input, width: "100%", boxSizing: "border-box", fontWeight: 800, color: "#0f172a" };
const panaSuggestionGrid = { maxHeight: "132px", overflowY: "auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(72px, 1fr))", gap: "6px", padding: "8px", border: "1px solid #dbeafe", borderRadius: "12px", background: "#eff6ff" };
const panaChip = { minHeight: "34px", border: "1px solid #bfdbfe", borderRadius: "10px", background: "#fff", color: "#1d4ed8", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", padding: "0 9px", fontWeight: 900, cursor: "pointer" };
const panaEmpty = { minHeight: "34px", display: "grid", placeItems: "center", color: "#dc2626", fontWeight: 800, fontSize: "12px" };
const marketSelectWrap = { height: "58px", border: "1px solid #cbd5e1", borderRadius: "12px", background: "linear-gradient(135deg,#eff6ff,#ffffff)", display: "grid", gridTemplateColumns: "1fr 24px", alignItems: "center", gap: "10px", padding: "0 14px" };
const marketSelect = { width: "100%", height: "100%", border: 0, outline: 0, background: "transparent", color: "#0f172a", fontSize: "16px", fontWeight: 700, appearance: "none" };
const buttonRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginTop: "24px" };
const showButton = { height: "56px", border: 0, borderRadius: "16px", background: "#f59e0b", color: "#fff", fontWeight: 800, fontSize: "16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" };
const saveButton = { height: "56px", border: 0, borderRadius: "16px", background: "#316ce8", color: "#fff", fontWeight: 800, fontSize: "16px", cursor: "pointer" };
const slotSection = { marginTop: "24px", display: "grid", gap: "14px" };
const slotTitleRow = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" };
const slotTitle = { color: "#0f172a", fontSize: "16px" };
const slotHint = { color: "#64748b", fontSize: "13px", fontWeight: 600 };
const slotButtonGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(138px, 1fr))", gap: 12 };
const slotButton = { minHeight: "64px", border: "1px solid #cbd5e1", borderRadius: "16px", background: "linear-gradient(180deg,#ffffff,#f8fafc)", color: "#0f172a", display: "grid", alignContent: "center", justifyItems: "center", gap: 5, cursor: "pointer", boxShadow: "0 8px 16px rgba(15,23,42,0.05)" };
const slotButtonActive = { ...slotButton, border: "1px solid #2563eb", background: "linear-gradient(135deg,#dbeafe,#eff6ff)", boxShadow: "0 12px 22px rgba(37,99,235,0.16)" };
const slotButtonDone = { ...slotButton, border: "1px solid #e5e7eb", background: "linear-gradient(180deg,#f8fafc,#eef2f7)", color: "#94a3b8", cursor: "not-allowed", boxShadow: "none" };
const slotButtonTime = { fontSize: 14, fontWeight: 900 };
const slotButtonState = { fontSize: 11, fontWeight: 800, color: "#64748b" };
const slotButtonStateActive = { ...slotButtonState, color: "#2563eb" };
const slotButtonStateDone = { ...slotButtonState, color: "#94a3b8" };
const timeSelectWrap = { height: "58px", border: "1px solid #cbd5e1", borderRadius: "12px", background: "linear-gradient(135deg,#eff6ff,#ffffff)", display: "grid", gridTemplateColumns: "24px 1fr 24px", alignItems: "center", gap: "10px", padding: "0 14px" };
const timeSelect = { width: "100%", height: "100%", border: 0, outline: 0, background: "transparent", color: "#0f172a", fontSize: "16px", fontWeight: 700, appearance: "none" };
const timePreviewChip = { width: "fit-content", padding: "6px 10px", borderRadius: "999px", background: "#dbeafe", color: "#1d4ed8", fontWeight: 800, fontSize: "12px" };
const tableTitleRow = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" };
const countText = { color: "#64748b", fontWeight: 600 };
const timelinePanel = { marginBottom: "20px", padding: "18px", borderRadius: "20px", border: "1px solid #dbeafe", background: "linear-gradient(180deg,#eff6ff 0%, #ffffff 100%)" };
const timelineHeader = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "14px", flexWrap: "wrap", marginBottom: "14px" };
const timelineTitle = { color: "#0f172a", fontSize: "16px", fontWeight: 900 };
const timelineSubtext = { color: "#64748b", fontSize: "12px", fontWeight: 600, marginTop: "4px" };
const timelineCountChip = { display: "inline-flex", alignItems: "center", minHeight: "32px", padding: "0 12px", borderRadius: "999px", background: "#dbeafe", color: "#1d4ed8", fontWeight: 900, fontSize: "12px" };
const timelineGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "12px" };
const declaredTimelineCard = { borderRadius: "18px", border: "1px dashed #cbd5e1", background: "linear-gradient(180deg,#ffffff,#f8fafc)", padding: "14px", display: "grid", gap: "10px", minHeight: "112px" };
const declaredTimelineCardDone = { ...declaredTimelineCard, border: "1px solid #bfdbfe", background: "linear-gradient(135deg,#dbeafe 0%, #eff6ff 50%, #ffffff 100%)" };
const declaredTimelineTop = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" };
const declaredTimelineTime = { color: "#0f172a", fontSize: "13px", fontWeight: 900 };
const declaredTimelineTimeDone = { ...declaredTimelineTime, color: "#1d4ed8" };
const declaredTimelineBadgePending = { display: "inline-flex", alignItems: "center", minHeight: "22px", padding: "0 9px", borderRadius: "999px", background: "#f8fafc", color: "#64748b", fontSize: "10px", fontWeight: 900, border: "1px solid #e2e8f0" };
const declaredTimelineBadgeDone = { ...declaredTimelineBadgePending, background: "#16a34a", color: "#fff", border: "1px solid #16a34a" };
const declaredTimelineBody = { display: "grid", gap: "8px", alignContent: "end", flex: 1 };
const declaredTimelineResult = { fontSize: "24px", fontWeight: 900, color: "#0f172a", letterSpacing: "0.02em" };
const declaredTimelinePending = { color: "#94a3b8", fontSize: "13px", fontWeight: 800 };
const declaredTimelineDelete = { width: "fit-content", border: 0, borderRadius: "10px", background: "#ef4444", color: "#fff", padding: "8px 12px", fontWeight: 800, cursor: "pointer", fontSize: "12px" };
const table = { width: "100%", borderCollapse: "collapse" };
const deleteButton = { border: 0, borderRadius: "10px", background: "#ef4444", color: "#fff", padding: "10px 16px", fontWeight: 700, cursor: "pointer" };
const modalBackdrop = { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)", display: "grid", placeItems: "center", zIndex: 1000, padding: "24px" };
const modalCard = { width: "100%", maxWidth: "920px", background: "#fff", borderRadius: "24px", overflow: "hidden", boxShadow: "0 24px 60px rgba(15, 23, 42, 0.24)" };
const modalHeader = { background: "linear-gradient(90deg, #2563eb, #4f46e5)", color: "#fff", padding: "22px 26px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const modalTitle = { fontSize: "18px", fontWeight: 800 };
const modalSubtitle = { marginTop: "6px", opacity: 0.95 };
const closeIcon = { width: "36px", height: "36px", border: 0, background: "transparent", color: "#fff", cursor: "pointer" };
const modalBody = { padding: "18px 22px 0" };
const modalFooter = { padding: "20px 22px 22px", display: "flex", justifyContent: "flex-end" };
const closeButton = { minWidth: "120px", height: "48px", border: 0, background: "#316ce8", color: "#fff", fontWeight: 800, cursor: "pointer" };
const valueStrong = { fontWeight: 800, color: "#0f172a" };
const winValue = { color: "#16a34a", fontWeight: 800 };
const emptyRow = { textAlign: "center", padding: "24px", color: "#64748b" };
const declaredGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(255px, 1fr))", gap: "14px" };
const declaredCard = { borderRadius: "18px", padding: "16px", background: "linear-gradient(180deg,#ffffff,#f8fafc)", border: "1px solid #e2e8f0", boxShadow: "0 12px 22px rgba(15,23,42,0.05)", display: "grid", gap: "14px" };
const declaredTop = { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" };
const declaredIndex = { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: "34px", height: "34px", borderRadius: "999px", background: "#eff6ff", color: "#2563eb", fontWeight: 900, fontSize: "12px" };
const declaredMarket = { color: "#0f172a", fontWeight: 900, fontSize: "14px", flex: 1 };
const declaredTime = { display: "inline-flex", alignItems: "center", minHeight: "28px", padding: "0 10px", borderRadius: "999px", background: "#f8fafc", color: "#475569", fontWeight: 800, fontSize: "11px", border: "1px solid #e2e8f0" };
const declaredBottom = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" };
const declaredResult = { color: "#0f172a", fontWeight: 900, fontSize: "24px", letterSpacing: "0.04em" };
const declaredDoneChip = { display: "inline-flex", alignItems: "center", minHeight: "28px", padding: "0 10px", borderRadius: "999px", background: "#dcfce7", color: "#15803d", fontWeight: 900, fontSize: "11px" };
const emptyResultCard = { borderRadius: "18px", border: "1px dashed #cbd5e1", background: "#fff", color: "#64748b", fontWeight: 700, padding: "22px", textAlign: "center" };

export default AdminStarline;
